import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../../api';
import { router } from '../../router';
import { t } from '../../i18n';
import { showToast } from '../../components/ui/toast';

interface Ref { id: string; name: string; }
interface OverviewPass {
  id: string; student_id: string; student_name: string;
  dance_type_name: string; level_name: string;
  competition_name: string; competition_date: string;
}

/** Irish Dance — admin bulk overview: who has passed what, filterable by dance type + level. */
@customElement('cadence-irish-dance-overview-page')
export class IrishDanceOverviewPage extends LitElement {
  @state() private types: Ref[] = [];
  @state() private levels: Ref[] = [];
  @state() private rows: OverviewPass[] = [];
  @state() private danceTypeId = '';
  @state() private levelId = '';
  @state() private loading = true;

  static styles = css`
    :host { display: block; }
    h1 { margin: 0 0 4px; font-size: 1.6rem; }
    p.sub { margin: 0 0 var(--space-5); color: var(--color-text-secondary); }
    .filters { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: var(--space-4); }
    .filters cadence-select { min-width: 200px; }
    a.student { color: var(--color-primary); text-decoration: none; }
    a.student:hover { text-decoration: underline; }
  `;

  connectedCallback(): void { super.connectedCallback(); this.init(); }

  private async init() {
    try {
      const [types, levels] = await Promise.all([api.get<Ref[]>('/dance/types'), api.get<Ref[]>('/dance/levels')]);
      this.types = types; this.levels = levels;
    } catch (e) { showToast((e as Error).message, 'error'); }
    await this.load();
  }

  private async load() {
    this.loading = true;
    try {
      const qs = new URLSearchParams();
      if (this.danceTypeId) qs.set('dance_type_id', this.danceTypeId);
      if (this.levelId) qs.set('level_id', this.levelId);
      const suffix = qs.toString() ? `?${qs}` : '';
      this.rows = await api.get<OverviewPass[]>(`/modules/irish-dance/passes/overview${suffix}`);
    } catch (e) { showToast((e as Error).message, 'error'); }
    finally { this.loading = false; }
  }

  private columns() {
    return [
      { key: 'student_name', label: t('irishDance.overview.colStudent'), render: (r: OverviewPass) => html`<a class="student" href="/admin/students/${r.student_id}" @click=${(e: Event) => { e.preventDefault(); router.navigate(`/admin/students/${r.student_id}`); }}>${r.student_name}</a>` },
      { key: 'dance_type_name', label: t('irishDance.overview.colDanceType') },
      { key: 'level_name', label: t('irishDance.overview.colLevel') },
      { key: 'competition_name', label: t('irishDance.overview.colCompetition') },
      { key: 'competition_date', label: t('irishDance.overview.colDate') },
    ];
  }

  render() {
    const allTypes = [{ value: '', label: t('irishDance.overview.allDanceTypes') }, ...this.types.map((x) => ({ value: x.id, label: x.name }))];
    const allLevels = [{ value: '', label: t('irishDance.overview.allLevels') }, ...this.levels.map((x) => ({ value: x.id, label: x.name }))];
    return html`
      <h1>${t('irishDance.overview.title')}</h1>
      <p class="sub">${t('irishDance.overview.sub')}</p>
      <div class="filters">
        <cadence-select label=${t('irishDance.overview.danceType')} .value=${this.danceTypeId} .options=${allTypes}
          @change=${(e: CustomEvent) => { this.danceTypeId = e.detail; this.load(); }}></cadence-select>
        <cadence-select label=${t('irishDance.overview.level')} .value=${this.levelId} .options=${allLevels}
          @change=${(e: CustomEvent) => { this.levelId = e.detail; this.load(); }}></cadence-select>
      </div>
      <cadence-card>
        ${this.loading
          ? html`<p style="color:var(--color-text-secondary)">${t('common.states.loading')}</p>`
          : this.rows.length
            ? html`<cadence-table .columns=${this.columns()} .rows=${this.rows}></cadence-table>`
            : html`<cadence-empty-state heading=${t('irishDance.overview.empty')}></cadence-empty-state>`}
      </cadence-card>
    `;
  }
}
